import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useRealtime } from '@/hooks/useRealtime';
import { useAuth } from '@/hooks/useAuth';
import AuthGuard from '@/components/feature/AuthGuard';
import OrganizerLayout from '../components/OrganizerLayout';

interface SponsorCategory {
  id: string;
  name: string;
  is_active: boolean;
}

interface Sponsor {
  id: string;
  category_id: string | null;
  name: string;
  website_url: string | null;
  logo_url: string | null;
  display_order: number;
  is_active: boolean;
  created_at: string;
  category_name?: string;
}

export default function OrganizerSponsors() {
  const { profile } = useAuth();
  const [tab, setTab] = useState<'sponsors' | 'categories'>('sponsors');
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [categories, setCategories] = useState<SponsorCategory[]>([]);
  const [allCategories, setAllCategories] = useState<SponsorCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', category_id: '', website_url: '', logo_url: '', display_order: 0, is_active: true });
  const [catForm, setCatForm] = useState({ name: '', display_order: 0, is_active: true });
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [spRes, catRes] = await Promise.all([
        supabase.from('sponsors').select('*').order('display_order', { ascending: true }),
        supabase.from('sponsor_categories').select('*').order('display_order'),
      ]);
      if (spRes.error) throw spRes.error;
      if (catRes.error) throw catRes.error;

      const catMap: Record<string, string> = {};
      (catRes.data || []).forEach((c: SponsorCategory) => { catMap[c.id] = c.name; });

      setSponsors((spRes.data || []).map((s) => ({ ...s, category_name: s.category_id ? (catMap[s.category_id] || 'Uncategorized') : 'Uncategorized' })));
      setAllCategories(catRes.data || []);
      setCategories((catRes.data || []).filter((c: SponsorCategory) => c.is_active));
      setError('');
    } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Failed to load data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useRealtime<Sponsor>({ table: 'sponsors', onAny: () => loadData() });
  useRealtime<SponsorCategory>({ table: 'sponsor_categories', onAny: () => loadData() });

  const toggleActive = async (id: string, current: boolean) => {
    try {
      const { error: err } = await supabase.from('sponsors').update({ is_active: !current }).eq('id', id);
      if (err) throw err;
    } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Failed'); }
  };

  const handleDeleteSponsor = async (id: string) => {
    try {
      const { error: err } = await supabase.from('sponsors').delete().eq('id', id);
      if (err) throw err;
    } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Failed to delete'); }
  };

  const handleSaveSponsor = async () => {
    if (!form.name.trim() || !form.category_id) {
      setError('Sponsor name and category are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editId) {
        const { error: err } = await supabase.from('sponsors').update({
          name: form.name.trim(), category_id: form.category_id, website_url: form.website_url.trim() || null,
          logo_url: form.logo_url.trim() || null, display_order: form.display_order, is_active: form.is_active,
        }).eq('id', editId);
        if (err) throw err;
      } else {
        const { error: err } = await supabase.from('sponsors').insert({
          name: form.name.trim(), category_id: form.category_id, website_url: form.website_url.trim() || null,
          logo_url: form.logo_url.trim() || null, display_order: form.display_order, is_active: form.is_active,
        });
        if (err) throw err;
      }
      setShowModal(false); setEditId(null);
      setForm({ name: '', category_id: '', website_url: '', logo_url: '', display_order: 0, is_active: true });
      setError('');
    } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleSaveCategory = async () => {
    if (!catForm.name.trim()) {
      setError('Category name is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editId) {
        const { error: err } = await supabase.from('sponsor_categories').update({
          name: catForm.name.trim(), display_order: catForm.display_order, is_active: catForm.is_active,
        }).eq('id', editId);
        if (err) throw err;
      } else {
        const { error: err } = await supabase.from('sponsor_categories').insert({
          name: catForm.name.trim(), display_order: catForm.display_order, is_active: catForm.is_active,
          created_by: profile?.id,
        });
        if (err) throw err;
      }
      setShowModal(false); setEditId(null);
      setCatForm({ name: '', display_order: 0, is_active: true });
      setError('');
    } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Failed to save category'); }
    finally { setSaving(false); }
  };

  const handleDeleteCategory = async (id: string) => {
    try {
      const { error: err } = await supabase.from('sponsor_categories').delete().eq('id', id);
      if (err) throw err;
    } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Failed to delete'); }
  };

  const openEditSponsor = (s: Sponsor) => {
    setEditId(s.id);
    setForm({ name: s.name, category_id: s.category_id || '', website_url: s.website_url || '', logo_url: s.logo_url || '', display_order: s.display_order || 0, is_active: s.is_active });
    setShowModal(true);
  };

  const openEditCategory = (c: SponsorCategory) => {
    setEditId(c.id);
    setCatForm({ name: c.name, display_order: c.display_order || 0, is_active: c.is_active });
    setShowModal(true);
  };

  return (
    <AuthGuard requiredRole="organizer">
      <OrganizerLayout title="Sponsors" breadcrumbs={[{ label: 'Dashboard', href: '/dashboard/organizer' }, { label: 'Sponsors' }]}>
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm flex items-center justify-between">
            <div className="flex items-start gap-2">
              <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
              <span>{error}</span>
            </div>
            <button onClick={() => setError('')} className="cursor-pointer flex-shrink-0 ml-2"><i className="ri-close-line"></i></button>
          </div>
        )}

        {/* Tab switcher */}
        <div className="flex items-center gap-2 mb-5">
          <div className="bg-background-100 rounded-full p-1 flex">
            <button onClick={() => setTab('sponsors')} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${tab === 'sponsors' ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>Sponsors</button>
            <button onClick={() => setTab('categories')} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${tab === 'categories' ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>Categories</button>
          </div>
          <div className="ml-auto">
            {tab === 'sponsors' ? (
              <button onClick={() => { setEditId(null); setForm({ name: '', category_id: '', website_url: '', logo_url: '', display_order: sponsors.length, is_active: true }); setShowModal(true); setError(''); }} className="px-4 py-2.5 rounded-lg bg-secondary-500 hover:bg-secondary-600 text-background-50 dark:text-foreground-950 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
                <i className="ri-add-line"></i> Add Sponsor
              </button>
            ) : (
              <button onClick={() => { setEditId(null); setCatForm({ name: '', display_order: allCategories.length, is_active: true }); setShowModal(true); setError(''); }} className="px-4 py-2.5 rounded-lg bg-secondary-500 hover:bg-secondary-600 text-background-50 dark:text-foreground-950 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
                <i className="ri-add-line"></i> Add Category
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="w-10 h-10 rounded-full border-2 border-secondary-500 border-t-transparent animate-spin"></div></div>
        ) : tab === 'categories' ? (
          allCategories.length === 0 ? (
            <div className="py-16 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-price-tag-3-line text-2xl text-foreground-300"></i>
              </div>
              <p className="text-foreground-400 text-sm">No sponsor categories yet</p>
            </div>
          ) : (
            <div className="bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-background-200/70 bg-background-100/50">
                      {['Category Name', 'Order', 'Active', 'Created', 'Actions'].map((l) => (
                        <th key={l} className="px-4 py-3 text-left text-xs font-semibold text-foreground-500 whitespace-nowrap">{l}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {allCategories.map((c) => (
                      <tr key={c.id} className={`border-b border-background-100 hover:bg-background-50/50 transition-colors ${!c.is_active ? 'opacity-50' : ''}`}>
                        <td className="px-4 py-3 font-medium text-foreground-900">{c.name}</td>
                        <td className="px-4 py-3 text-foreground-600 text-xs">{c.display_order || 0}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${c.is_active ? 'bg-accent-100 text-accent-700' : 'bg-background-200 text-foreground-400'}`}>
                            {c.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-foreground-500 text-xs whitespace-nowrap">{new Date(c.created_at || Date.now()).toLocaleDateString('en-IN')}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <button onClick={() => openEditCategory(c)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-secondary-50 text-foreground-400 hover:text-secondary-600 transition-colors cursor-pointer" title="Edit">
                              <i className="ri-edit-line text-sm"></i>
                            </button>
                            <button onClick={() => handleDeleteCategory(c.id)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-foreground-400 hover:text-red-600 transition-colors cursor-pointer" title="Delete">
                              <i className="ri-delete-bin-line text-sm"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )
        ) : (
          sponsors.length === 0 ? (
            <div className="py-16 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-building-line text-2xl text-foreground-300"></i>
              </div>
              <p className="text-foreground-400 text-sm">No sponsors yet</p>
            </div>
          ) : (
            <div className="bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-background-200/70 bg-background-100/50">
                      {['Sponsor', 'Category', 'Website', 'Active', 'Actions'].map((l) => (
                        <th key={l} className="px-4 py-3 text-left text-xs font-semibold text-foreground-500 whitespace-nowrap">{l}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sponsors.map((s) => (
                      <tr key={s.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {s.logo_url ? <img src={s.logo_url} alt={s.name} className="w-8 h-8 rounded object-contain bg-background-100" /> : (
                              <div className="w-8 h-8 rounded bg-background-100 flex items-center justify-center">
                                <i className="ri-building-line text-foreground-400 text-sm"></i>
                              </div>
                            )}
                            <span className="font-medium text-foreground-900">{s.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-foreground-600 text-xs">{s.category_name}</td>
                        <td className="px-4 py-3 text-xs">{s.website_url ? <a href={s.website_url} target="_blank" rel="noopener noreferrer" className="text-secondary-600 hover:text-secondary-700 whitespace-nowrap cursor-pointer">{(() => { try { return new URL(s.website_url).hostname; } catch { return s.website_url; } })()}</a> : '—'}</td>
                        <td className="px-4 py-3">
                          <button onClick={() => toggleActive(s.id, s.is_active)} className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${s.is_active ? 'bg-accent-500' : 'bg-background-300'}`}>
                            <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${s.is_active ? 'translate-x-5' : 'translate-x-0.5'}`}></div>
                          </button>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <button onClick={() => openEditSponsor(s)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-secondary-50 text-foreground-400 hover:text-secondary-600 transition-colors cursor-pointer" title="Edit">
                              <i className="ri-edit-line text-sm"></i>
                            </button>
                            <button onClick={() => handleDeleteSponsor(s.id)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-foreground-400 hover:text-red-600 transition-colors cursor-pointer" title="Delete">
                              <i className="ri-delete-bin-line text-sm"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )
        )}

        {/* Modal for sponsor */}
        {showModal && tab === 'sponsors' && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowModal(false)}>
            <div className="bg-background-50 rounded-2xl shadow-xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
              <div className="border-b border-background-200/70 p-5 flex items-center justify-between">
                <h3 className="font-heading text-lg font-bold text-foreground-950">{editId ? 'Edit' : 'Add'} Sponsor</h3>
                <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer">
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div><label className="block text-xs font-medium text-foreground-600 mb-1">Sponsor Name <span className="text-red-500">*</span></label>
                  <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-secondary-400 outline-none" placeholder="Sponsor name" />
                </div>
                <div><label className="block text-xs font-medium text-foreground-600 mb-1">Category <span className="text-red-500">*</span></label>
                  <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-700 text-sm focus:border-secondary-400 outline-none cursor-pointer">
                    <option value="">Select category...</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div><label className="block text-xs font-medium text-foreground-600 mb-1">Website URL</label>
                  <input type="url" value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-secondary-400 outline-none" placeholder="https://..." />
                </div>
                <div><label className="block text-xs font-medium text-foreground-600 mb-1">Logo Image URL</label>
                  <input type="url" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-secondary-400 outline-none" placeholder="https://..." />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-xs font-medium text-foreground-600 mb-1">Display Order</label>
                    <input type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-secondary-400 outline-none" min="0" />
                  </div>
                  <div className="flex items-center gap-3 pt-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="w-4 h-4 rounded accent-secondary-500" />
                      <span className="text-sm text-foreground-700">Active</span>
                    </label>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button onClick={() => setShowModal(false)} className="px-4 py-2 rounded-lg border border-background-200 text-foreground-600 text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Cancel</button>
                  <button onClick={handleSaveSponsor} disabled={saving} className="px-5 py-2 rounded-lg bg-secondary-500 hover:bg-secondary-600 disabled:bg-secondary-400 disabled:cursor-not-allowed text-background-50 dark:text-foreground-950 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
                    {saving && <div className="w-4 h-4 rounded-full border-2 border-background-50 border-t-transparent animate-spin"></div>}
                    {saving ? 'Saving...' : editId ? 'Save Changes' : 'Add Sponsor'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal for category */}
        {showModal && tab === 'categories' && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowModal(false)}>
            <div className="bg-background-50 rounded-2xl shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <div className="border-b border-background-200/70 p-5 flex items-center justify-between">
                <h3 className="font-heading text-lg font-bold text-foreground-950">{editId ? 'Edit' : 'Add'} Category</h3>
                <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer">
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div><label className="block text-xs font-medium text-foreground-600 mb-1">Category Name <span className="text-red-500">*</span></label>
                  <input type="text" value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-secondary-400 outline-none" placeholder="e.g. Platinum Sponsor" />
                </div>
                <div><label className="block text-xs font-medium text-foreground-600 mb-1">Display Order</label>
                  <input type="number" value={catForm.display_order} onChange={(e) => setCatForm({ ...catForm, display_order: parseInt(e.target.value) || 0 })} className="w-24 px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-secondary-400 outline-none" min="0" />
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={catForm.is_active} onChange={(e) => setCatForm({ ...catForm, is_active: e.target.checked })} className="w-4 h-4 rounded accent-secondary-500" />
                    <span className="text-sm text-foreground-700">Active</span>
                  </label>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button onClick={() => setShowModal(false)} className="px-4 py-2 rounded-lg border border-background-200 text-foreground-600 text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Cancel</button>
                  <button onClick={handleSaveCategory} disabled={saving} className="px-5 py-2 rounded-lg bg-secondary-500 hover:bg-secondary-600 disabled:bg-secondary-400 disabled:cursor-not-allowed text-background-50 dark:text-foreground-950 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
                    {saving && <div className="w-4 h-4 rounded-full border-2 border-background-50 border-t-transparent animate-spin"></div>}
                    {saving ? 'Saving...' : editId ? 'Save Changes' : 'Add Category'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </OrganizerLayout>
    </AuthGuard>
  );
}